import { submit_requestor_form_answers } from '@/services/requestor_portal_service';
import { NO_ANSWER_PROVIDED } from './view_case_details_answer_helpers';
import { error_message, normalize_text, to_array } from './view_case_details_helpers';

const FACT_INPUT_TYPES = [
  ['datetime', 'datetime-local'],
  ['date', 'date'],
  ['time', 'time'],
  ['currency', 'number'],
  ['decimal', 'number'],
  ['float', 'number'],
  ['integer', 'number'],
  ['number', 'number'],
  ['email', 'email'],
  ['phone', 'tel'],
  ['tel', 'tel'],
  ['url', 'url'],
];

export default {
  clear_fact_edit_success() {
    if (this.fact_edit_success_timeout_id) {
      clearTimeout(this.fact_edit_success_timeout_id);
      this.fact_edit_success_timeout_id = null;
    }
    this.fact_edit_success = '';
  },
  show_fact_edit_success(message) {
    this.clear_fact_edit_success();
    this.fact_edit_success = message;
    this.fact_edit_success_timeout_id = setTimeout(() => {
      this.fact_edit_success = '';
      this.fact_edit_success_timeout_id = null;
    }, 2000);
  },
  fact_section_key(column = {}) {
    return String(column?.section_key || column?.title || 'fact-section');
  },
  fact_row_key(fact = {}) {
    return String(fact?.section_question_id || `${fact.label || 'fact'}-${fact.value || ''}`);
  },
  can_edit_fact(fact = {}) {
    return Boolean(fact?.application_form_id && fact?.section_question_id);
  },
  is_select_fact(fact = {}) {
    return fact_type_has(fact, 'select') && !this.is_multi_select_fact(fact);
  },
  is_multi_select_fact(fact = {}) {
    return fact_type(fact).replace(/\s+/g, '_').includes('multi_select');
  },
  is_checkbox_fact(fact = {}) {
    return fact_type_has(fact, 'checkbox');
  },
  is_radio_fact(fact = {}) {
    return fact_type_has(fact, 'radio');
  },
  is_long_text_fact(fact = {}) {
    return ['long', 'text_area', 'textarea'].some((value) => fact_type_has(fact, value));
  },
  is_file_fact(fact = {}) {
    return ['file', 'upload', 'attachment', 'document', 'facial_verification']
      .some((value) => fact_type_has(fact, value));
  },
  accepted_file_types_for_fact(fact = {}) {
    return fact_type_has(fact, 'facial_verification') ? 'image/*' : undefined;
  },
  input_type_for_fact(fact = {}) {
    const match = FACT_INPUT_TYPES.find(([keyword]) => fact_type_has(fact, keyword));
    return match ? match[1] : 'text';
  },
  input_step_for_fact(fact = {}) {
    return ['number', 'float', 'decimal', 'currency'].some((value) => fact_type_has(fact, value))
      ? 'any'
      : undefined;
  },
  is_editing_fact_section(column = {}) {
    return this.editing_fact_section_key === this.fact_section_key(column);
  },
  is_saving_fact_section(column = {}) {
    return this.saving_fact_section_key === this.fact_section_key(column);
  },
  editable_facts_in_section(column = {}) {
    return to_array(column?.items).filter((fact) => this.can_edit_fact(fact));
  },
  can_edit_fact_section(column = {}) {
    return this.editable_facts_in_section(column).length > 0;
  },
  fact_section_needs_details(column = {}) {
    const facts = this.editable_facts_in_section(column);
    const missing_mandatory = facts.some(
      (fact) => fact?.is_mandatory && fact?.value === NO_ANSWER_PROVIDED
    );
    return missing_mandatory || (facts.length > 0 && facts.every(
      (fact) => fact?.value === NO_ANSWER_PROVIDED
    ));
  },
  open_first_incomplete_fact_section() {
    const incomplete_section = (this.backend_fact_columns || []).find(
      (column) => this.fact_section_needs_details(column)
    );
    if (incomplete_section) this.begin_fact_section_edit(incomplete_section);
  },
  begin_fact_section_edit(column = {}) {
    if (!this.can_edit_fact_section(column)) {
      return;
    }

    this.editing_fact_section_key = this.fact_section_key(column);
    this.editing_fact_answers = this.editable_facts_in_section(column).map((fact) => ({
      key: this.fact_row_key(fact),
      value: fact?.value === NO_ANSWER_PROVIDED ? '' : normalize_text(fact?.value),
      values: Array.isArray(fact?.values) ? [...fact.values] : [],
    }));
    this.fact_edit_error = '';
    this.fact_validation_errors = [];
    this.case_form_submission_error = '';
    this.case_form_submission_success = '';
    this.clear_fact_edit_success();
  },
  cancel_fact_section_edit() {
    this.editing_fact_section_key = '';
    this.editing_fact_answers = [];
    this.saving_fact_section_key = '';
    this.fact_edit_error = '';
    this.fact_validation_errors = [];
  },
  editing_fact_record(fact = {}) {
    return this.editing_fact_answers.find((record) => record.key === this.fact_row_key(fact)) || null;
  },
  editing_fact_value(fact = {}) {
    return this.editing_fact_record(fact)?.value || '';
  },
  editing_fact_values(fact = {}) {
    return this.editing_fact_record(fact)?.values || [];
  },
  update_fact_value(fact, value) {
    this.editing_fact_answers = update_fact_records(this, fact, { value });
  },
  update_fact_file_value(fact, files) {
    const file = Array.from(files || [])[0] || null;
    this.editing_fact_answers = update_fact_records(this, fact, { value: file });
  },
  update_fact_multi_value(fact, selected_options) {
    const values = Array.from(selected_options || []).map((option) => option.value);
    this.editing_fact_answers = update_fact_records(this, fact, { values });
  },
  update_fact_checkbox_value(fact, option_value, is_checked) {
    const values = checkbox_values(this.editing_fact_values(fact), option_value, is_checked);
    this.editing_fact_answers = update_fact_records(this, fact, { values });
  },
  serialized_fact_answer(fact = {}) {
    const value = this.editing_fact_value(fact);
    if (typeof File !== 'undefined' && value instanceof File) {
      return value.name;
    }

    if (this.is_multi_select_fact(fact) || this.is_checkbox_fact(fact)) {
      return this.editing_fact_values(fact).map(normalize_text).filter(Boolean).join(', ');
    }

    return normalize_text(this.editing_fact_value(fact));
  },
  async save_fact_section_edit(column = {}) {
    const editable_facts = this.editable_facts_in_section(column);
    if (!editable_facts.length) {
      return;
    }

    const answers = fact_answers(this, editable_facts);
    const missing_answers = answers.filter(
      (answer) => answer.is_mandatory && !normalize_text(answer.answer_text)
    );
    if (missing_answers.length) {
      this.fact_validation_errors = missing_answers.map((answer) => ({
        key: String(answer.section_question_id),
        message: `"${answer.label || 'This field'}" is required.`,
      }));
      this.fact_edit_error = 'Please complete all required fields before saving.';
      return;
    }

    await save_fact_answers(this, column, editable_facts, answers);
  },
};

function fact_type(fact = {}) {
  return normalize_text(fact?.question_type).toLowerCase();
}

function fact_type_has(fact = {}, keyword = '') {
  return fact_type(fact).includes(keyword);
}

function update_fact_records(component, fact, patch) {
  const fact_key = component.fact_row_key(fact);
  return component.editing_fact_answers.map((record) =>
    record.key === fact_key ? { ...record, ...patch } : record
  );
}

function checkbox_values(current_values = [], option_value, is_checked) {
  const normalized_option = normalize_text(option_value);
  return is_checked
    ? [...new Set([...current_values, normalized_option])]
    : current_values.filter((value) => value !== normalized_option);
}

function fact_answers(component, editable_facts = []) {
  return editable_facts
    .map((fact) => {
      const value = component.editing_fact_value(fact);
      return {
        section_question_id: Number(fact.section_question_id),
        question_id: Number(fact.question_id || 0) || null,
        answer_text: component.serialized_fact_answer(fact),
        ...(typeof File !== 'undefined' && value instanceof File ? { file: value } : {}),
        label: fact.label,
        is_mandatory: Boolean(fact.is_mandatory),
      };
    })
    .filter((answer) => answer.section_question_id);
}

async function save_fact_answers(component, column, editable_facts, answers) {
  component.saving_fact_section_key = component.fact_section_key(column);
  component.fact_edit_error = '';
  component.fact_validation_errors = [];
  component.clear_fact_edit_success();

  try {
    await submit_requestor_form_answers({
      application_form_id: Number(editable_facts[0].application_form_id),
      application_id: Number(component.selected_application_id),
      answers,
    });
    component.cancel_fact_section_edit();
    await component.load_case_facts();
    component.show_fact_edit_success(`${column.title || 'Section'} updated.`);
  } catch (error) {
    component.fact_edit_error = error_message(error, 'Failed to save this section.');
  } finally {
    component.saving_fact_section_key = '';
  }
}
